/**
 * NextToCallBlock — iter106 · Prochain candidat à appeler (CEO 2026-09-11)
 *
 * **Brief CEO** : la pré-recruteuse ouvre Prérec et n'a quasiment aucune
 * décision de tri à prendre. Elle voit une fiche condensée avec :
 *   - nom, offre, score, vague, signaux forts, 2-3 raisons, questions perso
 *   - CTA principal : "Appeler & préqualifier"
 *   - CTA secondaire : "Ouvrir la fiche complète"
 *   - Actions tracées : rappeler / indisponible / déjà traité / refus
 *
 * PAS de bouton "Passer au suivant" — chaque skip doit avoir un motif tracé.
 * Une fois l'action posée, le prochain candidat de la file remonte automatiquement.
 */
import { motion, AnimatePresence } from "framer-motion";
import {
    AlertCircle, Archive, CalendarClock, ExternalLink, FileText,
    Loader2, PhoneCall, Sparkles, UserX, XCircle, Ban,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import CallButton from "../call/CallButton";
import { api } from "../../lib/api";
import { formatDate } from "../../lib/format";


const CATEGORY_META = {
    top:       { color: "bg-yellow-50 text-yellow-800 border-yellow-300",     label: "TOP PROFIL",           emoji: "⭐" },
    promising: { color: "bg-emerald-50 text-emerald-800 border-emerald-300",   label: "TRÈS PROMETTEUR",      emoji: "🟢" },
    potential: { color: "bg-amber-50 text-amber-800 border-amber-300",         label: "POTENTIEL À VÉRIFIER", emoji: "🟡" },
    low:       { color: "bg-slate-100 text-slate-600 border-slate-300",        label: "NON PRIORITAIRE",      emoji: "🔴" },
};

const SIGNAL_LABELS = {
    academic_excellence: "Excellence académique",
    builder: "Builder / a créé",
    quantified_results: "Résultat quantifié",
    ai_digital_execution: "IA / Digital / Automatisation",
    growth_sales_execution: "Growth / Sales fort",
};

// Motifs de skip regroupés par action backend (source unique de vérité)
const SKIP_ACTIONS = [
    {
        key: "recall",
        icon: CalendarClock,
        label: "Programmer un rappel",
        color: "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100",
        needsDate: true,
    },
    {
        key: "unavailable",
        icon: UserX,
        label: "Injoignable / Plus disponible",
        color: "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100",
        setAsideReason: "candidat_hesite",
        setAsideLabel: "Injoignable / plus disponible",
    },
    {
        key: "already_processed",
        icon: Archive,
        label: "Déjà traité (mettre en vivier)",
        color: "bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100",
        setAsideReason: "priorite_autre",
        setAsideLabel: "Déjà traité par ailleurs",
    },
    {
        key: "reject",
        icon: XCircle,
        label: "Refuser définitivement",
        color: "bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100",
    },
];


export default function NextToCallBlock({ analytics, onActionDone }) {
    const [candidate, setCandidate] = useState(null);
    const [queueSize, setQueueSize] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [skipModalOpen, setSkipModalOpen] = useState(false);
    const [skipAction, setSkipAction] = useState(null);
    const [skipNote, setSkipNote] = useState("");
    const [skipDate, setSkipDate] = useState("");
    const [skipReason, setSkipReason] = useState("");
    const [rejectReasons, setRejectReasons] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    const loadNext = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await api.get("/growth/call-queue/next?limit=1");
            const next = data.next?.[0] || null;
            setCandidate(next);
            setQueueSize(data.queue_size || 0);
        } catch (e) {
            setError(e.response?.data?.detail || "Erreur de chargement");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadNext(); }, [loadNext]);

    // Charge les motifs de refus (source backend unique)
    useEffect(() => {
        api.get("/candidates/reject-reasons")
            .then(({ data }) => setRejectReasons(data.reasons || data || []))
            .catch(() => setRejectReasons([]));
    }, []);

    function openSkipModal(action) {
        setSkipAction(action);
        setSkipNote("");
        setSkipReason("");
        // Date par défaut : demain 09h00 (local)
        if (action.needsDate) {
            const d = new Date();
            d.setDate(d.getDate() + 1);
            d.setHours(9, 0, 0, 0);
            setSkipDate(d.toISOString().slice(0, 16));
        }
        setSkipModalOpen(true);
    }

    function closeSkipModal() {
        setSkipModalOpen(false);
        setSkipAction(null);
    }

    async function submitSkip() {
        if (!candidate || !skipAction) return;
        setSubmitting(true);
        try {
            if (skipAction.key === "recall") {
                // POST /api/growth/candidates/{id}/schedule-recall
                const iso = new Date(skipDate).toISOString();
                await api.post(`/growth/candidates/${candidate.id}/schedule-recall`, {
                    when: iso,
                    note: skipNote || null,
                });
                toast.success(`Rappel programmé pour ${new Date(skipDate).toLocaleString("fr-FR")}`);
            } else if (skipAction.key === "reject") {
                if (!skipReason) {
                    toast.error("Sélectionne un motif de refus");
                    setSubmitting(false);
                    return;
                }
                await api.post(`/candidates/${candidate.id}/reject`, {
                    reason: skipReason,
                    note: skipNote || null,
                });
                toast.success("Candidat refusé — trace ajoutée à l'historique");
            } else {
                // unavailable / already_processed → set-aside avec motif tracé
                await api.post(`/candidates/${candidate.id}/set-aside`, {
                    reason: skipAction.setAsideReason,
                    note: skipNote ? `${skipAction.setAsideLabel} · ${skipNote}` : skipAction.setAsideLabel,
                });
                toast.success(`Écarté · ${skipAction.setAsideLabel}`);
            }
            closeSkipModal();
            await loadNext();
            onActionDone?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Action impossible");
        } finally {
            setSubmitting(false);
        }
    }

    if (loading) {
        return (
            <div
                data-testid="growth-next-to-call-loading"
                className="rounded-2xl bg-white border-2 border-slate-100 p-8 flex items-center justify-center"
            >
                <Loader2 className="h-6 w-6 animate-spin text-slate-300" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-2xl bg-rose-50 border-2 border-rose-100 p-4 text-sm text-rose-700 font-semibold">
                {error}
            </div>
        );
    }

    if (!candidate) {
        // iter107 · CEO 2026-09-11 · Doctrine 4 catégories.
        // TOP + Très prometteur + Potentiel = appelable.
        // Le message ne peut annoncer "file vide" que si TOUT est scoré ET aucun
        // candidat appelable ne reste.
        const unscored = analytics?.unscored || 0;
        const totalCallable = analytics?.total_callable || 0;
        const prequalDone = analytics?.prequal_progress?.done || 0;

        if (unscored > 0) {
            // État 1 : scoring incomplet — la file NE PEUT PAS être vide
            const total = analytics?.total || 0;
            return (
                <div
                    data-testid="growth-next-to-call-scoring-pending"
                    className="rounded-2xl bg-amber-50 border-2 border-amber-200 p-6 text-center"
                >
                    <Loader2 className="h-8 w-8 text-amber-500 mx-auto mb-2 animate-spin" />
                    <p className="font-bold text-amber-800">
                        Analyse en cours — {unscored} candidatures restent à analyser avant que la file d'appels soit complète.
                    </p>
                    <p className="text-xs text-amber-700 mt-1">
                        {analytics?.scored || 0} / {total} candidatures analysées.
                        Clique sur "Analyser les {unscored} candidatures restantes" (bouton orange en haut, admin) si le batch n'est pas encore lancé.
                    </p>
                </div>
            );
        }

        if (totalCallable === 0) {
            // État 2 : tout scoré mais aucun candidat appelable (que des "non prioritaires")
            return (
                <div
                    data-testid="growth-next-to-call-no-callable"
                    className="rounded-2xl bg-slate-50 border-2 border-slate-200 p-6 text-center"
                >
                    <AlertCircle className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                    <p className="font-bold text-slate-700">
                        Aucun candidat à appeler pour le moment.
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                        Tous les candidats analysés sont classés "Non prioritaire" (score &lt; 40). Attends de nouvelles candidatures.
                    </p>
                </div>
            );
        }

        // État 3 : tout scoré, il y a des candidats appelables, mais tous traités ou en rappel
        return (
            <div
                data-testid="growth-next-to-call-empty"
                className="rounded-2xl bg-emerald-50 border-2 border-emerald-100 p-6 text-center"
            >
                <Sparkles className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
                <p className="font-bold text-emerald-800">
                    Bravo — file d'appels vide ! ({prequalDone}/{totalCallable} préqualifiés)
                </p>
                <p className="text-xs text-emerald-600 mt-1">
                    Tous les candidats appelables ont été traités ou ont un rappel programmé. Ils réapparaîtront automatiquement le moment venu.
                </p>
            </div>
        );
    }

    const cat = CATEGORY_META[candidate.growth_category] || CATEGORY_META.potential;
    const signals = candidate.growth_strong_signals || [];
    const forces = candidate.growth_forces_preuves || [];
    const questions = candidate.growth_priority_questions || [];

    return (
        <>
            <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="growth-next-to-call-card"
                className="rounded-2xl bg-white border-2 border-shariti-orange/40 p-5 shadow-lg relative overflow-hidden"
            >
                {/* accent stripe */}
                <div className="absolute top-0 left-0 w-1.5 h-full bg-gradient-to-b from-shariti-orange to-rose-500" />

                {/* Header */}
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                        <div className="inline-flex items-center gap-2 px-2 py-0.5 rounded-full bg-shariti-orange/10 text-shariti-orange text-[10px] font-black uppercase tracking-widest mb-1.5">
                            <PhoneCall className="h-3 w-3" /> Prochain candidat à appeler
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <h2 className="text-2xl font-black text-shariti-black tracking-tight truncate">
                                {candidate.first_name} {candidate.last_name}
                            </h2>
                            {/* iter108 · CV très visible directement à côté du nom */}
                            {candidate.cv_url ? (
                                <a
                                    href={candidate.cv_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="growth-next-to-call-cv-btn"
                                    title="Ouvrir le CV dans un nouvel onglet"
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-black bg-shariti-orange text-white border-2 border-shariti-orange hover:bg-shariti-orange-hover shadow-sm transition"
                                >
                                    <FileText className="h-3.5 w-3.5" />
                                    Voir le CV
                                    <ExternalLink className="h-3 w-3" />
                                </a>
                            ) : (
                                <span
                                    data-testid="growth-next-to-call-cv-btn-disabled"
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-black bg-slate-100 text-slate-400 border-2 border-slate-200 cursor-not-allowed"
                                    title="Aucun CV attaché"
                                >
                                    <FileText className="h-3.5 w-3.5" />
                                    Pas de CV
                                </span>
                            )}
                        </div>
                        <div className="text-xs font-semibold text-slate-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                            <span>{candidate.phone || "—"}</span>
                            {candidate.email && <> · <span className="font-normal">{candidate.email}</span></>}
                            {candidate.city && <> · <span className="font-normal">{candidate.city}</span></>}
                        </div>
                        <div className="text-[11px] font-semibold text-slate-400 mt-0.5">
                            {candidate.layan_offer_title || "—"}
                            {candidate.layan_offer_region && (
                                <span className="ml-1.5 px-1.5 py-0.5 rounded bg-shariti-orange/10 text-shariti-orange text-[9px] font-black uppercase tracking-widest">
                                    {candidate.layan_offer_region}
                                </span>
                            )}
                            {" · Postulé " + formatDate(candidate.applied_at)}
                        </div>
                    </div>

                    {/* Score + Wave */}
                    <div className="flex items-center gap-2 shrink-0">
                        <div className="flex flex-col items-center">
                            <div className="w-14 h-14 rounded-full bg-white border-4 border-shariti-orange shadow flex flex-col items-center justify-center">
                                <span className="text-xl font-black leading-none text-shariti-orange">
                                    {candidate.growth_score}
                                </span>
                                <span className="text-[9px] font-black leading-none text-shariti-orange">/100</span>
                            </div>
                            <span className={`mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest border ${cat.color}`}>
                                <span>{cat.emoji}</span>
                                <span>{cat.label}</span>
                            </span>
                        </div>
                    </div>
                </div>

                {/* Strong signals */}
                {signals.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5" data-testid="growth-next-to-call-signals">
                        {signals.map((s) => (
                            <span
                                key={s}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest bg-emerald-50 text-emerald-800 border border-emerald-200"
                            >
                                ✓ {SIGNAL_LABELS[s] || s}
                            </span>
                        ))}
                    </div>
                )}

                {/* Summary */}
                {candidate.growth_summary && (
                    <div className="mt-3 text-sm text-slate-700 leading-relaxed">
                        <div className="flex items-start gap-2">
                            <Sparkles className="h-4 w-4 text-shariti-orange shrink-0 mt-0.5" />
                            <p className="italic line-clamp-3">{candidate.growth_summary}</p>
                        </div>
                    </div>
                )}

                {/* Preuves (top 2-3) */}
                {forces.length > 0 && (
                    <div className="mt-3 bg-emerald-50/60 border border-emerald-100 rounded-xl p-2.5">
                        <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700 mb-1">
                            Raisons principales (preuves observées)
                        </div>
                        <ul className="space-y-1">
                            {forces.slice(0, 3).map((p, i) => (
                                <li key={i} className="text-[11px] text-emerald-900 font-medium leading-snug">
                                    📄 {p}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Questions perso (2 premières visibles, reste replié) */}
                {questions.length > 0 && (
                    <details className="mt-3 group" data-testid="growth-next-to-call-questions">
                        <summary className="cursor-pointer text-[10px] font-black uppercase tracking-widest text-shariti-orange hover:text-shariti-orange-hover">
                            🎯 Questions personnalisées à poser ({questions.length})
                        </summary>
                        <ul className="mt-2 space-y-1.5 pl-1">
                            {questions.map((q, i) => (
                                <li key={i} className="text-[11px] text-slate-700 font-medium italic leading-snug">
                                    Q{i + 1} · « {q} »
                                </li>
                            ))}
                        </ul>
                    </details>
                )}

                {/* CTAs */}
                <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                        {queueSize} candidat{queueSize > 1 ? "s" : ""} dans la file (TOP + Très prometteur + Potentiel)
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        {candidate.phone && (
                            <CallButton
                                candidate={candidate}
                                variant="wide"
                                dataTestid="growth-next-to-call-call-btn"
                            />
                        )}
                        <Link
                            to={`/growth/${candidate.id}/prequal`}
                            data-testid="growth-next-to-call-prequal-btn"
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-black bg-shariti-orange text-white hover:bg-shariti-orange-hover transition"
                        >
                            <PhoneCall className="h-3.5 w-3.5" />
                            Appeler & préqualifier
                        </Link>
                        {/* iter109b · CEO 2026-09-12 · "Ouvrir la fiche complète" retiré :
                             menait à /candidats/{id} = univers recruteur/donateur/contrat
                             qui n'a rien à voir avec le pipeline Growth. Tout ce dont la RH
                             a besoin (email, téléphone, CV, notes, historique appels) est
                             accessible via /growth/:id/prequal ci-dessus. */}
                    </div>
                </div>

                {/* Skip actions (avec motif tracé) */}
                <div className="mt-3 pt-3 border-t border-dashed border-slate-100">
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">
                        Si tu ne peux pas préqualifier ce candidat maintenant :
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {SKIP_ACTIONS.map((a) => {
                            const Icon = a.icon;
                            return (
                                <button
                                    key={a.key}
                                    onClick={() => openSkipModal(a)}
                                    data-testid={`growth-next-to-call-skip-${a.key}`}
                                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold border transition ${a.color}`}
                                >
                                    <Icon className="h-3.5 w-3.5" />
                                    {a.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </motion.div>

            {/* Skip Modal */}
            <AnimatePresence>
                {skipModalOpen && skipAction && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={closeSkipModal}
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
                    >
                        <motion.div
                            initial={{ scale: 0.96, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.96, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                            data-testid="growth-skip-modal"
                            className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-5"
                        >
                            <div className="flex items-center gap-2 mb-3">
                                <skipAction.icon className="h-5 w-5 text-shariti-orange" />
                                <h3 className="text-lg font-black text-shariti-black">{skipAction.label}</h3>
                            </div>
                            <p className="text-xs text-slate-500 mb-4">
                                Candidat : <b>{candidate.first_name} {candidate.last_name}</b> · {CATEGORY_META[candidate.growth_category]?.label || "—"} · score {candidate.growth_score}
                            </p>

                            {skipAction.needsDate && (
                                <div className="mb-3">
                                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">
                                        Rappeler à
                                    </label>
                                    <input
                                        type="datetime-local"
                                        value={skipDate}
                                        onChange={(e) => setSkipDate(e.target.value)}
                                        data-testid="growth-skip-recall-date"
                                        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-shariti-orange"
                                    />
                                    <p className="text-[10px] text-slate-400 mt-1">
                                        Le candidat réapparaîtra automatiquement dans la file à ce moment.
                                    </p>
                                </div>
                            )}

                            {skipAction.key === "reject" && (
                                <div className="mb-3">
                                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">
                                        Motif de refus <span className="text-rose-500">*</span>
                                    </label>
                                    <select
                                        value={skipReason}
                                        onChange={(e) => setSkipReason(e.target.value)}
                                        data-testid="growth-skip-reject-reason"
                                        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-shariti-orange"
                                    >
                                        <option value="">— Sélectionne un motif —</option>
                                        {rejectReasons.map((r) => (
                                            <option key={r.value} value={r.value}>
                                                {r.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div className="mb-4">
                                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">
                                    Note (optionnelle)
                                </label>
                                <textarea
                                    value={skipNote}
                                    onChange={(e) => setSkipNote(e.target.value)}
                                    rows={3}
                                    data-testid="growth-skip-note"
                                    placeholder="Détail utile pour la prochaine RH…"
                                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-shariti-orange"
                                />
                            </div>

                            <div className="flex items-center gap-2 justify-end">
                                <button
                                    onClick={closeSkipModal}
                                    className="px-3 py-2 rounded-full text-xs font-black text-slate-600 hover:bg-slate-100 transition"
                                >
                                    Annuler
                                </button>
                                <button
                                    onClick={submitSkip}
                                    disabled={submitting || (skipAction.needsDate && !skipDate) || (skipAction.key === "reject" && !skipReason)}
                                    data-testid="growth-skip-submit"
                                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-black bg-shariti-orange text-white hover:bg-shariti-orange-hover disabled:opacity-50 transition"
                                >
                                    {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                                    Confirmer
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
